/**
 * Azhara cart line merge.
 *
 * Shopify only merges cart lines when variant AND line-item properties match. Product
 * pages add diffusers with free-oil properties (_freebie_*, Freebie, Freebie Size) while
 * quick-adds (cards, drawer upsells, landing pages) add the bare variant, so the same
 * product could end up on two lines. This script:
 *   1. Intercepts every fetch POST to /cart/add(.js): if the cart already holds the same
 *      variant on a line whose properties are only free-oil ones (or none), it raises that
 *      line's quantity through /cart/change.js instead of adding a new line. When the new
 *      add carries the free-oil choice and the existing line has none, the choice is
 *      attached to that line. Adds carrying any other properties or a selling plan pass
 *      through untouched.
 *   2. On page load, folds any existing duplicate lines of that kind into one.
 * Must load before the header's fetch wrapper (it is included in <head>, not deferred).
 */
(function () {
  'use strict';
  if (window.__azhCartMerge || typeof window.fetch !== 'function') return;
  window.__azhCartMerge = true;

  var nativeFetch = window.fetch.bind(window);
  var chain = Promise.resolve();
  var FREEBIE = /^(_freebie|Freebie)/;

  function onlyFreebieProps(props) {
    if (!props) return true;
    return Object.keys(props).every(function (k) { return FREEBIE.test(k); });
  }
  function hasFreebie(props) {
    return !!props && Object.keys(props).some(function (k) { return FREEBIE.test(k) && props[k] !== ''; });
  }
  function isAddUrl(url) { return /\/cart\/add(\.js)?(\?|$)/.test(url); }
  function jsonHeaders() { return { 'Content-Type': 'application/json', 'Accept': 'application/json' }; }

  /* Read { id, quantity, properties, selling_plan } from a JSON body or FormData; null = cannot merge */
  function parseAdd(body) {
    try {
      if (typeof body === 'string') {
        var j = JSON.parse(body);
        if (j.items) {
          if (j.items.length !== 1) return null;
          j = j.items[0];
        }
        if (!j.id) return null;
        return { id: parseInt(j.id, 10), quantity: parseInt(j.quantity || 1, 10), properties: j.properties || {}, selling_plan: j.selling_plan || null };
      }
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        var props = {};
        var plan = null;
        body.forEach(function (v, k) {
          var m = /^properties\[(.+)\]$/.exec(k);
          if (m) props[m[1]] = v;
          if (k === 'selling_plan' && v) plan = v;
        });
        var id = parseInt(body.get('id'), 10);
        if (!id) return null;
        return { id: id, quantity: parseInt(body.get('quantity') || 1, 10), properties: props, selling_plan: plan };
      }
    } catch (e) { /* fall through */ }
    return null;
  }

  function getCart() {
    return nativeFetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }
  function change(payload) {
    return nativeFetch('/cart/change.js', { method: 'POST', headers: jsonHeaders(), credentials: 'same-origin', body: JSON.stringify(payload) });
  }
  function mergeable(item, variantId) {
    return item.variant_id === variantId && !item.selling_plan_allocation && onlyFreebieProps(item.properties);
  }

  function tryMerge(add) {
    return getCart().then(function (cart) {
      var line = null;
      (cart.items || []).forEach(function (it) { if (!line && mergeable(it, add.id)) line = it; });
      if (!line) return null;
      var payload = { id: line.key, quantity: line.quantity + (add.quantity || 1) };
      if (hasFreebie(add.properties) && !hasFreebie(line.properties)) payload.properties = add.properties;
      return change(payload).then(function (res) {
        if (!res.ok) return null;
        return res.json().then(function (newCart) {
          var merged = null;
          (newCart.items || []).forEach(function (it) { if (!merged && it.variant_id === add.id && onlyFreebieProps(it.properties)) merged = it; });
          /* Shape the reply like /cart/add.js so existing callers keep working */
          return new Response(JSON.stringify(merged || {}), { status: 200, headers: { 'Content-Type': 'application/json' } });
        });
      });
    });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST' || !isAddUrl(url) || !init || !init.body) return nativeFetch(input, init);
    var add = parseAdd(init.body);
    if (!add || add.selling_plan || !onlyFreebieProps(add.properties)) return nativeFetch(input, init);

    var run = function () {
      return tryMerge(add).then(function (res) { return res || nativeFetch(input, init); }, function () { return nativeFetch(input, init); });
    };
    var p = chain.then(run, run);
    chain = p.then(function () {}, function () {});
    return p;
  };

  /* Fold duplicate lines already sitting in a cart (from before this fix) */
  function consolidate() {
    getCart().then(function (cart) {
      var groups = {};
      (cart.items || []).forEach(function (it) {
        if (it.selling_plan_allocation || !onlyFreebieProps(it.properties)) return;
        (groups[it.variant_id] = groups[it.variant_id] || []).push(it);
      });
      var jobs = [];
      Object.keys(groups).forEach(function (vid) {
        var lines = groups[vid];
        if (lines.length < 2) return;
        var keep = lines.filter(function (l) { return hasFreebie(l.properties); })[0] || lines[0];
        var total = lines.reduce(function (s, l) { return s + l.quantity; }, 0);
        lines.forEach(function (l) { if (l !== keep) jobs.push({ id: l.key, quantity: 0 }); });
        jobs.push({ id: keep.key, quantity: total });
      });
      if (!jobs.length) return;
      jobs.reduce(function (p, job) { return p.then(function () { return change(job); }); }, Promise.resolve()).then(function () {
        if (window.refreshHeaderCart) window.refreshHeaderCart();
        if (window.refreshSideCart) window.refreshSideCart();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        if (/\/cart(\?|$)/.test(location.pathname)) location.reload();
      });
    }).catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', consolidate);
  else consolidate();
})();
